import type { UserManager } from "oidc-react";
import { isPublicRoute } from "@/constants";

/** Which Keycloak realm a UserManager singleton serves. */
export type AuthRealm = "lantbrukare" | "b2b";

const LOG_PREFIX: Record<AuthRealm, string> = {
  lantbrukare: "[Lantbrukare Auth]",
  b2b: "[B2B Auth]",
};

/**
 * OIDC callback routes. These are deliberately absent from PUBLIC_ROUTES —
 * they need an AuthProvider to process the callback — but they must not drive
 * token renewal of their own: `silent_redirect_uri` is an SPA route, so the
 * hidden renewal iframe boots the whole app and constructs its own
 * UserManagers. Left unguarded, that iframe arms an expiry timer against the
 * same stored user and can nest a further silent renewal inside itself.
 */
export const AUTH_CALLBACK_PREFIXES = ["/authentication/", "/b2b/authentication/"];

/**
 * Decide whether a realm owns the route currently in the address bar.
 *
 * Both UserManager singletons are constructed at module import time and both
 * subscribe to token events, so every handler must first establish that it is
 * the one responsible for the current page.
 *
 * @param realm - the realm whose handler is asking
 * @returns true when this realm should act on the current route
 */
const handlesCurrentRoute = (realm: AuthRealm): boolean => {
  const { pathname } = window.location;

  if (isPublicRoute(pathname)) return false;
  if (AUTH_CALLBACK_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return false;
  }

  const isB2bRoute = pathname.startsWith("/b2b");
  return realm === "b2b" ? isB2bRoute : !isB2bRoute;
};

/**
 * Wire access-token lifecycle handling onto a realm's UserManager.
 *
 * Algorithm:
 * 1. Ignore any event raised while a public route is open. `/share/:token`
 *    renders without an AuthProvider and needs no token, so a redirect there
 *    throws the visitor off a page that never required a login (AG-282).
 * 2. Ignore events belonging to the other realm's UserManager.
 * 3. On expiry, attempt exactly one silent sign-in. If it fails, drop the
 *    stored user and stop. Redirecting from a background handler yanks the user
 *    off whatever page they happen to be on; oidc-react's `autoSignIn` sends
 *    them to Keycloak the next time a protected page is actually mounted, and
 *    `onBeforeSignIn` preserves the URL so they land back on it.
 *
 * @param userManager - the realm's UserManager singleton
 * @param realm - which realm this manager serves
 */
export const registerTokenExpiryHandlers = (
  userManager: UserManager,
  realm: AuthRealm,
): void => {
  const prefix = LOG_PREFIX[realm];

  // "Expiring soon" fires every token lifecycle and is routine, so it stays at
  // debug level: the default console then shows only real failures below.
  userManager.events.addAccessTokenExpiring(() => {
    if (!handlesCurrentRoute(realm)) return;
    console.debug(
      `${prefix} Access token expiring soon, automatic renewal will be attempted`,
    );
  });

  userManager.events.addAccessTokenExpired(() => {
    if (!handlesCurrentRoute(realm)) return;

    console.warn(`${prefix} Access token expired, attempting silent sign-in`);
    userManager.signinSilent().catch((error: unknown) => {
      // Deliberately no signinRedirect() here — see step 3 above.
      console.error(
        `${prefix} Silent sign-in failed, clearing stored session:`,
        error,
      );
      void userManager.removeUser();
    });
  });

  userManager.events.addSilentRenewError((error: Error) => {
    if (!handlesCurrentRoute(realm)) return;
    console.error(`${prefix} Silent token renewal error:`, error);
  });
};
